'use client'

import { useState, useEffect } from 'react'
import { X, Cookie, Shield, BarChart3 } from 'lucide-react'

export function getCookieConsent() {
  if (typeof window === 'undefined') return 'undecided'

  const consent = localStorage.getItem('cookie_consent')
  return consent || 'undecided'
}

export function setCookieConsent(consent: 'accepted' | 'declined') {
  if (typeof window === 'undefined') return

  localStorage.setItem('cookie_consent', consent)
  localStorage.setItem('cookie_consent_date', new Date().toISOString())
}

export default function CookieBanner() {
  const [consentGiven, setConsentGiven] = useState<'undecided' | 'accepted' | 'declined'>(
    'undecided'
  )
  const [showDetails, setShowDetails] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    // Delay cookie banner to prevent LCP blocking
    const timer = setTimeout(() => {
      // Avoid hydration error by checking consent after mount
      const consent = getCookieConsent()
      setConsentGiven(consent as 'undecided' | 'accepted' | 'declined')

      // Show banner if consent is undecided
      if (consent === 'undecided') {
        setIsVisible(true)
        // Add smooth fade-in animation
        setTimeout(() => setIsAnimating(true), 50)
      }
    }, 1000) // 1 second delay to ensure LCP is not blocked

    return () => clearTimeout(timer)
  }, [])

  const handleAcceptAll = () => {
    setCookieConsent('accepted')
    setConsentGiven('accepted')
    // Smooth fade-out animation
    setIsAnimating(false)
    setTimeout(() => setIsVisible(false), 300)

    // Enable Meta Pixel tracking
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('consent', 'grant')
      // Track consent acceptance
      window.fbq('track', 'AcceptCookies')
    }
  }

  const handleDeclineAll = () => {
    setCookieConsent('declined')
    setConsentGiven('declined')
    // Smooth fade-out animation
    setIsAnimating(false)
    setTimeout(() => setIsVisible(false), 300)

    // Disable Meta Pixel tracking
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('consent', 'revoke')
    }
  }

  const handleCustomize = () => {
    setShowDetails(!showDetails)
  }

  if (!isVisible || consentGiven !== 'undecided') {
    return null
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Cookie Banner */}
      <div
        className={`fixed right-0 bottom-0 left-0 z-50 border-t border-gray-700 bg-[#212121] shadow-2xl transition-all duration-300 ${
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
      >
        <div className="mx-auto max-w-7xl p-6 sm:p-8">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="mb-4 flex items-center gap-3">
                <Cookie className="h-6 w-6 text-[#ff4500]" />
                <h3 className="text-xl font-bold text-white">Wir verwenden Cookies</h3>
              </div>

              <p className="mb-6 text-base leading-relaxed text-gray-200">
                Wir nutzen Cookies und ähnliche Technologien, um Ihnen die bestmögliche Erfahrung
                auf unserer Website zu bieten. Dazu gehören notwendige Cookies für den Betrieb der
                Website sowie Marketing-Cookies zur Verbesserung unserer Dienstleistungen.
              </p>

              {showDetails && (
                <div className="mb-6 rounded-lg border border-gray-700 bg-gray-800 p-5">
                  <h4 className="mb-4 font-bold text-white">Cookie-Details:</h4>

                  <div className="space-y-4 text-sm">
                    <div className="flex items-start gap-3">
                      <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" />
                      <div>
                        <div className="font-bold text-white">Notwendige Cookies</div>
                        <div className="text-gray-300">
                          Erforderlich für die Grundfunktionen der Website
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <BarChart3 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#ff4500]" />
                      <div>
                        <div className="font-bold text-white">Marketing & Analytics</div>
                        <div className="text-gray-300">
                          Meta Pixel zur Messung der Werbewirksamkeit und Verbesserung unserer
                          Marketingkampagnen
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={handleAcceptAll}
                  className="rounded-lg bg-[#ff4500] px-8 py-3 font-bold text-white shadow-lg transition-all hover:scale-105 hover:bg-[#e03e00]"
                >
                  Alle akzeptieren
                </button>

                <button
                  onClick={handleDeclineAll}
                  className="rounded-lg bg-gray-700 px-8 py-3 font-bold text-white transition-all hover:scale-105 hover:bg-gray-600"
                >
                  Alle ablehnen
                </button>

                <button
                  onClick={handleCustomize}
                  className="rounded-lg border-2 border-gray-600 px-6 py-3 font-bold text-gray-200 transition-all hover:border-[#ff4500] hover:text-white"
                >
                  {showDetails ? 'Details ausblenden' : 'Details anzeigen'}
                </button>
              </div>

              <p className="mt-4 text-sm text-gray-400">
                Weitere Informationen finden Sie in unserer{' '}
                <a
                  href="/datenschutzrichtlinie"
                  className="font-medium text-[#ff4500] underline hover:text-[#e03e00]"
                >
                  Datenschutzerklärung
                </a>{' '}
                und den{' '}
                <a
                  href="/geschaftsbedingungen"
                  className="font-medium text-[#ff4500] underline hover:text-[#e03e00]"
                >
                  Nutzungsbedingungen
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
